'use client';

import { useEffect, useState } from 'react';

import { usePathname as useNextPathname, useSearchParams as useNextSearchParams } from 'next/navigation';

const useWindowPathname = () => {
    const [pathname, setPathname] = useState(typeof window !== 'undefined' ? window.location.pathname : '');

    useEffect(() => {
        const handlePopState = () => {
            setPathname(window.location.pathname);
        };
        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    return pathname;
};

/**
 * Returns the current pathname of current page.
 * ⚠️ NOTE: use it as a last resort when you need to get pathname both in React app and in Next.js
 * or you only need first part of pathname which does not change in browser.
 * Prefer using `usePathname` from `next/navigation` or `useLocation` from `react-router`.
 */
export let usePathname: () => string;

if (process.env.IS_NEXT === 'true') {
    usePathname = useNextPathname;
} else {
    usePathname = useWindowPathname;
}

type SearchParamsHook = (
    initialParams?: string | string[][] | Record<string, string> | URLSearchParams,
) => URLSearchParams;

const useWindowSearchParams: SearchParamsHook = (initialParams) => {
    const [searchParams, setSearchParams] = useState(
        new URLSearchParams(typeof window !== 'undefined' ? window.location.search : initialParams),
    );

    useEffect(() => {
        const handlePopState = () => {
            setSearchParams(new URLSearchParams(window.location.search));
        };
        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    return searchParams;
};

/** @function useSearchParams
 * ⚠️ NOTE:
 * * When Next.js's useRouter/Link is primarily use for navigation the - useNextSearchParams will work as expected & you can use the Doppelgänger hook without issues.
 * * ❗ However, if your application directly uses native browser methods to change the URL (e.g., window.history.pushState), consider using useWindowSearchParams to ensure up-to-date behavior.
 *  */
export let useSearchParams: SearchParamsHook;

if (process.env.IS_NEXT === 'true') {
    useSearchParams = (initialParams) => {
        const paramsSSR = useNextSearchParams();

        return useWindowSearchParams(initialParams ?? paramsSSR.toString());
    };
} else {
    useSearchParams = useWindowSearchParams;
}
